// Payment authorisation chain (§9) shared by Send money and Pay via UPI:
// Non-UPI rails use MPIN only; UPI uses the UPI PIN, setting one first
// when needed. Resolves to an auth ticket handed to onTicket.
import { useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { generateStepUpOtp, verifyStepUpOtp } from '@/api/auth';
import { beginPaymentAuth, verifyPaymentMpin } from '@/api/payments';
import { MOCK_PAYMENTS } from '@/api/flags';
import { hasUpiPin, setMockUpiPin, verifyUpiPin } from '@/api/upi';
import { ApiError } from '@/api/client';
import type { PaymentRail } from '@/types/phase2.types';
import { isEasyPin } from '@/utils/phase2Validation';

export type PaymentAuthState = 'IDLE' | 'MPIN' | 'OTP' | 'UPI_SET' | 'UPI_CONFIRM' | 'UPI_PIN';

export interface PaymentAuthOptions {
  /** The MPIN travels in the payment request body: no MPIN sign-in and no
   *  OTP after it. Send money only; other flows keep MPIN then OTP. */
  mpinInPaymentBody?: boolean;
}

export const usePaymentAuth = (
  onTicket: (authTicket: string, pin?: string) => void | Promise<void>,
  options: PaymentAuthOptions = {},
) => {
  const [authState, setAuthState] = useState<PaymentAuthState>('IDLE');
  const [pinError, setPinError] = useState('');
  const [pinResetKey, setPinResetKey] = useState(0);
  const [pinLength, setPinLength] = useState<4 | 6>(6);
  const otpExpiry = 180;
  const otpSessionKey = 0;
  const authIdRef = useRef('');
  const pendingUpiPinRef = useRef('');
  const stepUpRef = useRef<{ transactionId: string; deliveryMethod: string; transactionType: string; purpose: string } | null>(null);
  const approvalLineRef = useRef('');
  const pinRef = useRef('');

  const fail = (err: unknown) => {
    setPinError(err instanceof ApiError ? err.message : 'Something went wrong. Try again.');
    setPinResetKey((k) => k + 1);
  };

  /** Start the chain for a rail. Returns '' on success, an error message otherwise. */
  const start = async (rail: PaymentRail, approvalLine: string, upiVpa?: string): Promise<string> => {
    setPinError('');
    approvalLineRef.current = approvalLine;
    try {
      if (rail === 'UPI') {
        if (!upiVpa) return 'A UPI ID is required to verify the UPI PIN.';
        const vpa = upiVpa;
        authIdRef.current = vpa;
        setAuthState((await hasUpiPin()) ? 'UPI_PIN' : 'UPI_SET');
      } else {
        const session = await beginPaymentAuth(approvalLine);
        authIdRef.current = session.authId;
        setAuthState('MPIN');
      }
      return '';
    } catch (err) {
      return err instanceof ApiError ? err.message : 'Could not start the payment.';
    }
  };

  const completePin = async (pin: string) => {
    setPinError('');
    try {
      if (authState === 'MPIN' && options.mpinInPaymentBody) {
        // The mock ledger consumes a ticket from its own MPIN check; the real
        // server checks the MPIN sent with the payment.
        const ticket = MOCK_PAYMENTS ? (await verifyPaymentMpin(authIdRef.current, pin)).authTicket : authIdRef.current;
        pinRef.current = pin;
        await onTicket(ticket, pin);
        setAuthState('IDLE');
      } else if (authState === 'MPIN') {
        const { authTicket } = await verifyPaymentMpin(authIdRef.current, pin);
        pinRef.current = pin;
        const otp = await generateStepUpOtp({
          transactionId: authIdRef.current,
          transactionType: 'PAYMENT',
          deliveryMethod: 'SMS',
          purpose: approvalLineRef.current,
        });
        stepUpRef.current = { ...otp, transactionType: 'PAYMENT', purpose: approvalLineRef.current };
        authIdRef.current = authTicket;
        setAuthState('OTP');
      } else if (authState === 'UPI_SET') {
        if (isEasyPin(pin)) {
          setPinError('That UPI PIN is too easy to guess.');
          setPinResetKey((k) => k + 1);
          return;
        }
        pendingUpiPinRef.current = pin;
        setAuthState('UPI_CONFIRM');
      } else if (authState === 'UPI_CONFIRM') {
        if (pin !== pendingUpiPinRef.current) {
          setPinError('UPI PINs do not match.');
          setPinResetKey((k) => k + 1);
          return;
        }
        await setMockUpiPin(pin);
        const { authTicket } = await verifyUpiPin({ vpa: authIdRef.current, pin });
        pinRef.current = pin;
        toast.success('UPI PIN set');
        const otp = await generateStepUpOtp({
          transactionId: authIdRef.current || `upi-${Date.now()}`,
          transactionType: 'UPI_PAYMENT',
          deliveryMethod: 'SMS',
          purpose: approvalLineRef.current,
        });
        stepUpRef.current = { ...otp, transactionType: 'UPI_PAYMENT', purpose: approvalLineRef.current };
        authIdRef.current = authTicket;
        setAuthState('OTP');
      } else if (authState === 'UPI_PIN') {
        const { authTicket } = await verifyUpiPin({ vpa: authIdRef.current, pin });
        pinRef.current = pin;
        const otp = await generateStepUpOtp({
          transactionId: authIdRef.current || `upi-${Date.now()}`,
          transactionType: 'UPI_PAYMENT',
          deliveryMethod: 'SMS',
          purpose: approvalLineRef.current,
        });
        stepUpRef.current = { ...otp, transactionType: 'UPI_PAYMENT', purpose: approvalLineRef.current };
        authIdRef.current = authTicket;
        setAuthState('OTP');
      }
    } catch (err) {
      fail(err);
    }
  };

  const verifyOtp = async (code: string) => {
    const session = stepUpRef.current;
    if (!session) return;
    try {
      const result = await verifyStepUpOtp({
        transactionId: session.transactionId,
        code,
        transactionType: session.transactionType,
        deliveryMethod: session.deliveryMethod,
        purpose: session.purpose,
      });
      stepUpRef.current = null;
      setAuthState('IDLE');
      await onTicket(String(result.authTicket ?? result.token ?? authIdRef.current), pinRef.current);
    } catch (err) {
      fail(err);
    }
  };

  const resendOtp = async () => {
    const session = stepUpRef.current;
    if (!session) return 0;
    const fresh = await generateStepUpOtp({
      transactionId: session.transactionId,
      transactionType: session.transactionType,
      deliveryMethod: session.deliveryMethod,
      purpose: session.purpose,
    });
    stepUpRef.current = { ...session, ...fresh };
    return fresh.expiresInSeconds ?? 180;
  };

  const close = () => setAuthState('IDLE');

  return {
    authState,
    pinError,
    pinLength,
    setPinLength,
    pinResetKey,
    otpExpiry,
    otpSessionKey,
    start,
    completePin,
    verifyOtp,
    resendOtp,
    close,
  };
};
